import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type React from "react";
import { useState } from "react";
import { QuestionnairePdfEmailConfirmModal } from "./QuestionnairePdfEmailConfirmModal";
import { type QuestionnairePdfType, useQuestionnairePdf } from "./useQuestionnairePdf";

export const QuestionnaireMoreActions = () => {
  const { t } = useResource();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmType, setConfirmType] = useState<QuestionnairePdfType | null>(null);
  const { clientEmail, isSending, sendPdf, openPreview } = useQuestionnairePdf();
  const isSendDisabled = !clientEmail || isSending.empty;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        aria-label={t(Resources.Questionnaire.MoreActions.Title)}
        aria-controls={anchorEl ? "questionnaire-more-actions" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="questionnaire-more-actions"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={!!anchorEl}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { mt: 1, border: "1px solid", borderColor: "divider", borderRadius: Radius.M },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            openPreview("empty");
          }}
          sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto" }}
        >
          <ListItemIcon>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t(Resources.Questionnaire.MoreActions.ShowPdf)} slotProps={{ primary: { fontSize: 14 } }} />
        </MenuItem>
        <MenuItem
          disabled={isSendDisabled}
          onClick={() => {
            if (isSendDisabled) return;
            handleClose();
            setConfirmType("empty");
          }}
          sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto" }}
        >
          <ListItemIcon>
            <EmailOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Tooltip title={!clientEmail ? t(Resources.Questionnaire.Evaluation.SendPdf.EmailInvalid) : ""} placement="top" arrow>
            {/* A disabled MenuItem swallows pointer events, so the tooltip needs its own target - clicks on it still bubble up. */}
            <Box sx={{ flexGrow: 1, pointerEvents: "auto" }}>
              <ListItemText primary={t(Resources.Questionnaire.MoreActions.SendEmail)} slotProps={{ primary: { fontSize: 14 } }} />
            </Box>
          </Tooltip>
        </MenuItem>
      </Menu>

      <QuestionnairePdfEmailConfirmModal
        type={confirmType}
        clientEmail={clientEmail}
        onClose={() => setConfirmType(null)}
        onConfirm={sendPdf}
      />
    </>
  );
};
